import { BlockNavigation, Button, CharField, Flex, H1, useBreadcrumbs } from "@inmagik/magik-ui"
import { Formik } from "formik"
import useModalTrigger from "magik-react-hooks//useModalTrigger"
import { useMemo, useState } from "react"
import { Container } from "react-bootstrap"
import { useNavigate, useParams } from "react-router-dom"
import BottomFormFixed from "../../components/BottomFormFixed"
import Layout from "../../components/Layout"
import ModalDelete from "../../components/ModalDelete"
import { ScrollLayout } from "../../components/ScrollLayout"
import { useEdizioneCreate, useEdizioneUpdate, useEdizioniList } from "../../hooks/edizioni"
import { useQsFilters } from "../../hooks/filters"
import { useOpera, useOperaDelete, useOperaUpdate } from "../../hooks/opere"
import { FiltersBase, Opera } from "../../types"
import EdizioneModal from "../Edizioni/EdizioneModal"
import { TableEdizioni } from "../Edizioni/TableEdizioni"
import { initialValuesOpera, validationSchemaOpera } from "./OperaCommon"
import OperaForm from "./OperaForm"
import { useAuth } from "../../auth"
import { transformErrorForForm } from "../../utils/utils"

const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "",
  } as FiltersBase)

export default function OpereUpdate() {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { id } = useParams()
  const { data: opera } = useOpera(Number(id))
  const { data: edizioni } = useEdizioniList({
    opera: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })

  const updateOpera = useOperaUpdate(Number(id))
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState("dettaglio")
  const [modalEdizioneCreateState, modalEdizioneCreateActions] = useModalTrigger()
  const [modalEdizioneUpdateState, modalEdizioneUpdateActions] = useModalTrigger()

  const [modalDeleteOperaState, modalDeleteOperaActions] = useModalTrigger()

  const createEdizione = useEdizioneCreate()
  const updateEdizione = useEdizioneUpdate()

  const deleteOpera = useOperaDelete()

  const initialValues = useMemo(() => initialValuesOpera(opera), [opera])
  const validationSchema = useMemo(() => validationSchemaOpera(), [])

  useBreadcrumbs([{ label: "Opere", href: "/opere/opere" }, { label: "Dettaglio Opera" }])

  const { user } = useAuth()

  return (
    <Layout>
      <ScrollLayout hasBottomFixed>
        <Flex alignItems="center" justifyContent="between">
          <H1>{opera?.titolo || <>&nbsp;</>}</H1>
          {activeTab === "edizioni" && user?.can_delete_data && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalEdizioneCreateActions.open()
              }}
            />
          )}
          {activeTab === "dettaglio" && (
            <Button
              size="md"
              variant="danger"
              title="Elimina"
              leadingIcon="delete"
              onClick={() => {
                modalDeleteOperaActions.open()
              }}
            />
          )}
        </Flex>
        <ScrollLayout.Tabs className="mt-6" onTabChange={(tab) => setActiveTab(tab)}>
          <ScrollLayout.Tab id="dettaglio" label="Dettaglio" key="dettaglio">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, actions) => {
                return updateOpera.mutateAsync(values as Opera).then(() => {
                  navigate(`/opere/opere`)
                }).catch((e) => {
                  actions.setErrors(transformErrorForForm(e))
                })
              }}
            >
              {({ handleSubmit, dirty, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <Container fluid className="mt-6" style={{ marginBottom: "48px" }}>
                    <OperaForm />
                    <BottomFormFixed url="/edizioni/edizioni" />
                  </Container>
                  {/* <BlockNavigation
                    when={dirty && !isSubmitting}
                    message="Sei sicuro di voler uscire? Le modifiche non salvate andranno perse."
                    title="Attenzione"
                    proceedText="Esci"
                    cancelText="Resta"
                  /> */}
                </form>
              )}
            </Formik>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="edizioni" label="Edizioni" key="edizioni">
            <div className="mt-3">
              <Flex className="mb-3">
                <CharField
                  label=""
                  placeholder="Cerca..."
                  leadingIcon="search"
                  style={{
                    width: 300,
                  }}
                  value={filters.search}
                  onChange={(value) => {
                    setFilters({ ...filters, search: value })
                  }}
                />
              </Flex>
              <TableEdizioni
                edizioni={edizioni?.results || []}
                setFilters={setFilters}
                filters={uiFilters}
                count={edizioni?.count || 0}
                fullCount={edizioni?.full_count || 0}
                showOpera={false}
                actionLink={modalEdizioneUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
        </ScrollLayout.Tabs>
      </ScrollLayout>
      {modalEdizioneCreateState.isOpen && (
        <EdizioneModal
          show={modalEdizioneCreateState.isOpen}
          onClose={modalEdizioneCreateActions.close}
          save={(values) => {
            return createEdizione
              .mutateAsync({
                ...values,
                opera: Number(id),
              })
              .then(() => {
                modalEdizioneCreateActions.close()
              })
          }}
          title={"Aggiungi edizione"}
        />
      )}
      {modalEdizioneUpdateState.isOpen && (
        <EdizioneModal
          show={modalEdizioneUpdateState.isOpen}
          onClose={modalEdizioneUpdateActions.close}
          edizione={modalEdizioneUpdateState.value}
          save={(values) => {
            return updateEdizione
              .mutateAsync({
                ...values,
                opera: Number(id),
              })
              .then(() => {
                modalEdizioneUpdateActions.close()
              })
          }}
          title={"Modifica edizione"}
        />
      )}
      <ModalDelete
        isOpen={modalDeleteOperaState.isOpen}
        onClose={modalDeleteOperaActions.close}
        onDelete={() => {
          return deleteOpera.mutateAsync(Number(id)).then(() => {
            navigate("/opere/opere")
          })
        }}
        title="Elimina opera"
        message="Se
          i sicuro di voler eliminare l'opera?"
      />
    </Layout>
  )
}
